"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.sanitizingUsPhoneNumberTransformer = exports.usPhoneNumberTransformer = exports.UsPhoneNumber = exports.PhoneNumber = exports.UsPhoneNumberFormat = void 0;
const assert_1 = __importDefault(require("assert"));
const assertUtils_1 = require("./assertUtils");
var UsPhoneNumberFormat;
(function (UsPhoneNumberFormat) {
    /** standard format at Aven: "9084321642" */
    UsPhoneNumberFormat["standard"] = "standard";
    /** national format with country code: "19084321642" */
    UsPhoneNumberFormat["nationalWithCountryCode"] = "nationalWithCountryCode";
    /** national format with international prefix: "+9084321642" */
    UsPhoneNumberFormat["nationalWithInternationalPrefix"] = "nationalWithInternationalPrefix";
    /** international format: "+19084321642" */
    UsPhoneNumberFormat["international"] = "international";
    /** detects a supported format automatically */
    UsPhoneNumberFormat["unknownFormat"] = "unknownFormat";
})(UsPhoneNumberFormat || (exports.UsPhoneNumberFormat = UsPhoneNumberFormat = {}));
const getStandardFormatFromConstructionParams = (params) => {
    switch (params.format) {
        case UsPhoneNumberFormat.standard:
            (0, assert_1.default)(/^\d{10}$/.test(params.phoneNumber), `expected standard US phone number to contain exactly ten digits, found ${params.phoneNumber}`);
            return params.phoneNumber;
        case UsPhoneNumberFormat.nationalWithCountryCode:
            (0, assert_1.default)(/^1\d{10}$/.test(params.phoneNumber), `expected US phone number with country code to contain 1 followed by ten digits, found ${params.phoneNumber}`);
            return params.phoneNumber.substring(1);
        case UsPhoneNumberFormat.nationalWithInternationalPrefix:
            (0, assert_1.default)(UsPhoneNumber.isNationalWithInternationalPrefix({ phoneNumber: params.phoneNumber }), `expected US phone number with international prefix to contain + followed by ten digits, found ${params.phoneNumber}`);
            return params.phoneNumber.substring(1);
        case UsPhoneNumberFormat.international:
            (0, assert_1.default)(/^\+1\d{10}$/.test(params.phoneNumber), `expected international US phone number to contain +1 followed by ten digits, found ${params.phoneNumber}`);
            return params.phoneNumber.substring(2);
        case UsPhoneNumberFormat.unknownFormat:
            if (/^\d{10}$/.test(params.phoneNumber)) {
                return getStandardFormatFromConstructionParams({ format: UsPhoneNumberFormat.standard, phoneNumber: params.phoneNumber });
            }
            else if (/^1\d{10}$/.test(params.phoneNumber)) {
                return getStandardFormatFromConstructionParams({ format: UsPhoneNumberFormat.nationalWithCountryCode, phoneNumber: params.phoneNumber });
            }
            else if (UsPhoneNumber.isNationalWithInternationalPrefix({ phoneNumber: params.phoneNumber })) {
                return getStandardFormatFromConstructionParams({ format: UsPhoneNumberFormat.nationalWithInternationalPrefix, phoneNumber: params.phoneNumber });
            }
            else {
                (0, assert_1.default)(/^\+1\d{10}$/.test(params.phoneNumber), `expected unknown-format US phone number to contain ten digits, 1 followed by ten digits, + followed by ten digits, or +1 followed by ten digits, found ${params.phoneNumber}`);
                return getStandardFormatFromConstructionParams({ format: UsPhoneNumberFormat.international, phoneNumber: params.phoneNumber });
            }
        default:
            throw (0, assertUtils_1.assertNever)(params, 'unhandled US phone number format');
    }
};
class PhoneNumber {
    standardFormat;
    constructor(standardFormat) {
        this.standardFormat = standardFormat;
    }
    getStandardFormat = () => this.standardFormat;
    /**
     * @deprecated USE WITH CAUTION
     *
     * This will not be a public method in the long term. It is only for use during the migration where we need to connect PhoneNumber
     * with parts of the system that are still using strings and naming conventions. Explicitly choose getStandardFormat() or another
     * public string-formatting method instead.
     */
    toString = () => this.getStandardFormat();
    // JSON serialization must preserve the string shape expected by existing API consumers.
    toJSON = () => this.getStandardFormat();
}
exports.PhoneNumber = PhoneNumber;
class UsPhoneNumber extends PhoneNumber {
    isUsPhoneNumber = true;
    constructor(params) {
        super(getStandardFormatFromConstructionParams(params));
    }
    getFormatWithInternationalCode = () => `+1${this.getStandardFormat()}`;
    static isNationalWithInternationalPrefix = ({ phoneNumber }) => {
        return /^\+\d{10}$/.test(phoneNumber);
    };
}
exports.UsPhoneNumber = UsPhoneNumber;
exports.usPhoneNumberTransformer = {
    to: (value) => {
        return value?.getStandardFormat() ?? null;
    },
    from: (value) => {
        return value === null || value === undefined ? null : new UsPhoneNumber({ format: UsPhoneNumberFormat.unknownFormat, phoneNumber: value });
    },
};
/**
 * Sanitizes formatted US phone number strings before constructing a UsPhoneNumber.
 * Unlike usPhoneNumberTransformer, it accepts common formatting characters and returns null for values it cannot normalize.
 */
exports.sanitizingUsPhoneNumberTransformer = {
    to: exports.usPhoneNumberTransformer.to,
    from: (value) => {
        if (value === null || value === undefined || !/^\+?[\d\s().-]+$/.test(value)) {
            return null;
        }
        try {
            return new UsPhoneNumber({
                format: UsPhoneNumberFormat.unknownFormat,
                phoneNumber: value.replace(/[\s().-]/g, ''),
            });
        }
        catch {
            return null;
        }
    },
};
